import React, { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown } from "lucide-react";

export interface SelectFieldProps {
    label?: string;
    id?: string;
    name?: string;
    value?: string;
    options: Array<{ value: string; label: string }>;
    placeholder?: string;
    required?: boolean;
    readOnly?: boolean;
    disabled?: boolean;
    helpText?: string;
    error?: string;
    className?: string;
    containerClassName?: string;
    layout?: "stacked" | "horizontal";
    onChange?: (event: React.ChangeEvent<HTMLSelectElement>) => void;
    onBlur?: (event: React.FocusEvent<HTMLSelectElement>) => void;
    onFocus?: (event: React.FocusEvent<HTMLSelectElement>) => void;
}

export const SelectField: React.FC<SelectFieldProps> = ({
    label,
    id,
    name,
    value = "",
    options = [],
    placeholder,
    required = false,
    readOnly = false,
    disabled = false,
    helpText,
    error,
    className = "",
    containerClassName = "",
    layout = "horizontal",
    onChange,
    onBlur,
    onFocus,
}) => {
    const fieldId = id || name || `select-${Math.random().toString(36).slice(2, 11)}`;
    const [isOpen, setIsOpen] = useState(false);
    const rootRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const handlePointerDown = (event: MouseEvent | TouchEvent) => {
            if (!rootRef.current?.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", handlePointerDown);
        document.addEventListener("touchstart", handlePointerDown);
        document.addEventListener("keydown", handleEscape);

        return () => {
            document.removeEventListener("mousedown", handlePointerDown);
            document.removeEventListener("touchstart", handlePointerDown);
            document.removeEventListener("keydown", handleEscape);
        };
    }, []);

    const selectedOption = useMemo(
        () => options.find((option) => option.value === value),
        [options, value]
    );

    const createSelectEvent = (nextValue: string) =>
        ({
            target: {
                value: nextValue,
                name: name || fieldId,
                id: fieldId,
            },
            currentTarget: {
                value: nextValue,
                name: name || fieldId,
                id: fieldId,
            },
        } as unknown as React.ChangeEvent<HTMLSelectElement>);

    const createFocusEvent = () =>
        ({
            target: {
                value,
                name: name || fieldId,
                id: fieldId,
            },
            currentTarget: {
                value,
                name: name || fieldId,
                id: fieldId,
            },
        } as unknown as React.FocusEvent<HTMLSelectElement>);

    const handleSelect = (nextValue: string) => {
        if (disabled || readOnly) {
            return;
        }

        onChange?.(createSelectEvent(nextValue));
        setIsOpen(false);
        onBlur?.(createFocusEvent());
    };

    const toggleDropdown = () => {
        if (disabled || readOnly) {
            return;
        }

        onFocus?.(createFocusEvent());
        setIsOpen((open) => !open);
    };

    const triggerClasses = [
        "relative w-full rounded-xl border bg-surface pl-3 pr-14 py-2 text-left shadow-sm transition-colors",
        "focus:outline-none focus:ring-2 focus:ring-border-focus focus:border-border-focus",
        error
            ? "border-error-300 focus:ring-error-500 focus:border-error-500"
            : readOnly || disabled
                ? "cursor-not-allowed border-border-primary bg-surface-secondary text-text-tertiary"
                : "border-border-primary text-text-primary hover:border-border-secondary",
        className,
    ]
        .filter(Boolean)
        .join(" ");

    const containerClasses =
        layout === "horizontal"
            ? `grid grid-cols-1 gap-2 md:grid-cols-3 md:gap-4 items-start ${containerClassName}`
            : `space-y-2 ${containerClassName}`;

    const labelClasses =
        layout === "horizontal"
            ? "md:col-span-1 block text-sm font-medium text-text-secondary"
            : "block text-sm font-medium text-text-secondary";

    const selectContainerClasses = layout === "horizontal" ? "md:col-span-2" : "";

    return (
        <div className={containerClasses}>
            {label && (
                <div className={layout === "horizontal" ? "md:col-span-1" : ""}>
                    <label htmlFor={fieldId} className={labelClasses}>
                        {label} {required && <span className="text-error-500">*</span>}
                    </label>
                </div>
            )}

            <div className={selectContainerClasses}>
                <div className="relative" ref={rootRef}>
                    <input
                        type="hidden"
                        id={fieldId}
                        name={name || fieldId}
                        value={value}
                        disabled={disabled || readOnly}
                    />

                    <button
                        type="button"
                        className={triggerClasses}
                        onClick={toggleDropdown}
                        aria-expanded={isOpen}
                        aria-haspopup="listbox"
                        aria-controls={`${fieldId}-listbox`}
                        disabled={disabled || readOnly}
                    >
                        <span
                            className={`block truncate ${
                                selectedOption ? "text-text-primary" : "text-text-tertiary"
                            }`}
                        >
                            {selectedOption?.label || placeholder || ""}
                        </span>
                        <ChevronDown
                            className={`pointer-events-none absolute right-5 top-1/2 h-5 w-5 -translate-y-1/2 text-text-tertiary transition-transform ${
                                isOpen ? "rotate-180" : ""
                            }`}
                        />
                    </button>

                    {isOpen && (
                        <div
                            id={`${fieldId}-listbox`}
                            role="listbox"
                            className="absolute left-0 right-0 top-full z-30 mt-2 overflow-hidden rounded-xl border border-border-primary bg-surface shadow-lg"
                        >
                            {placeholder && !selectedOption && (
                                <div className="border-b border-border-primary px-4 py-3 text-sm text-text-tertiary">
                                    {placeholder}
                                </div>
                            )}

                            <div className="max-h-64 overflow-y-auto py-1">
                                {options.map((option) => {
                                    const isSelected = option.value === value;

                                    return (
                                        <button
                                            key={option.value}
                                            type="button"
                                            role="option"
                                            aria-selected={isSelected}
                                            className={`flex w-full items-center justify-between gap-3 px-4 py-3 text-left text-sm transition-colors ${
                                                isSelected
                                                    ? "bg-hover-medium text-text-primary"
                                                    : "text-text-secondary hover:bg-hover-light"
                                            }`}
                                            onClick={() => handleSelect(option.value)}
                                        >
                                            <span className="truncate">{option.label}</span>
                                            {isSelected && <Check className="h-4 w-4 shrink-0 text-accent-primary" />}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>

                {(helpText || error) && (
                    <p
                        id={`${fieldId}-help`}
                        className={`mt-1 text-xs ${error ? "text-error-600" : "text-text-tertiary"}`}
                    >
                        {error || helpText}
                    </p>
                )}
            </div>
        </div>
    );
};

export default SelectField;
